// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { SpoolingSettings } from 'components/Autodesk/extensions/SpoolsExtension/types';
import type { AutodeskDbId } from 'components/Autodesk/types';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNil } from 'helpers/isNotNil';
import { strongValues } from 'helpers/strongEntries';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';

/**
 * Isolates the focused DB IDs, hides and locks selection for any spools or spool requests that are already in the model, and optionally fits to view.
 * @param spoolingSettings The settings for spooling, including whether to fit the view to the isolated elements.
 */
export const useRequestModeIsolation = (spoolingSettings: SpoolingSettings) => {
  const { fitToView: shouldFitToView } = spoolingSettings;
  const { commands } = useAutodeskExtensionStoreSnapshot('apsViewer');
  const spools = useAutodeskExtensionStoreSnapshot('spools');
  const { spoolRequests } = useSpoolRequestsViewerState();
  return useCallback(
    (focusedDbIds: AutodeskDbId[] | null, isRequesting: boolean) => {
      if (isNil(spools) || isNil(spoolRequests)) return;
      // Isolate the focused DB IDs if they exist
      commands?.isolate(focusedDbIds ?? [], shouldFitToView);

      const spoolRequestSpools = spoolRequests.map(([_, spool]) => spool);

      // Get the list of DBIDS that are already spooled or already in a spool request.
      // If the user is requesting new spools, these should be hidden and unselectable.
      const unrequestableDbIds = [...strongValues(spools), ...spoolRequestSpools].flatMap(({ objects }) =>
        objects.map(({ dbId }) => dbId),
      );
      if (isRequesting) {
        // hide/lockSelection have no store equivalents yet — still go through this.viewer directly
        commands?.hide(unrequestableDbIds);
        commands?.lockSelection(unrequestableDbIds, true);
      } else {
        // When the user is exiting request mode, unhide everything and unlock everything.
        commands?.show(unrequestableDbIds);
        commands?.lockSelection(unrequestableDbIds, false);
      }
    },
    [spools, spoolRequests, commands, shouldFitToView],
  );
};
